import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { api, tokenStore, formatApiErrorDetail } from '@/lib/api';
import { setMode as setAudioStoreMode } from '@/lib/audioCue';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = unauthenticated, object = user
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // If returning from Emergent OAuth (session_id in hash), AuthCallback
        // will exchange it and call refresh() itself — skip the /me race.
        if (
            typeof window !== 'undefined' &&
            window.location.hash &&
            window.location.hash.includes('session_id=')
        ) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        if (data?.access_token) tokenStore.set(data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (email, password, name) => {
        const { data } = await api.post('/auth/register', {
            email,
            password,
            name,
        });
        // /register returns the user directly + sets httpOnly cookie. We also
        // fetch /me to confirm and unify the shape.
        await checkAuth();
        return data;
    }, [checkAuth]);

    const logout = useCallback(async () => {
        try {
            await api.post('/auth/logout');
        } catch {
            // best-effort
        }
        tokenStore.clear();
        setUser(false);
    }, []);

    // Keep the audio-cue pub/sub store in sync with the user's saved
    // cue_mode whenever the user object loads/changes. Without this, the
    // floating RestTimer would always reset to localStorage's stale value
    // after profile edits.
    useEffect(() => {
        const mode = user && user.workout_cue_prefs?.cue_mode;
        if (mode) setAudioStoreMode(mode);
    }, [user]);

    const updateProfile = useCallback(async (patch) => {
        const { data } = await api.patch('/auth/me', patch);
        setUser(data);
        return data;
    }, []);

    const value = useMemo(
        () => ({
            user,
            loading,
            isAuthenticated: !!user,
            login,
            register,
            logout,
            updateProfile,
            refresh: checkAuth,
        }),
        [user, loading, login, register, logout, updateProfile, checkAuth]
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}

export { formatApiErrorDetail };
